import { useContext, useEffect, useMemo, useRef, useState } from "react";
import DropDown from "src/component/DropDown";
import { useGet } from "src/hooks/useGet";
import { GetHospitalPickupLookup } from "src/requests/Shared";
import { useTranslation } from "react-i18next";
import { dateFormatter, getRemainingDays } from "src/utils";
import { Context } from "src/context";
import { REQUESTS_TABS } from ".";
import { Alert } from "src/component/Alert";
import { useLocation } from "react-router-dom";

type PickupDates = {
    id: number;
    pickupDate: string;
    startDueDate: string;
    endDueDate: string;
    isDone: boolean;
};

type PickupDatesOptions = Omit<PickupDates, "id"> & {
    label: string;
    value: number;
};

interface IPickupCard {
    selectedTab: number;
    setSelectedTab: (tab: number) => void;
    setDisabledTabs: (tabs: number[]) => void;
}

const PickupCard = ({ selectedTab, setSelectedTab, setDisabledTabs }: IPickupCard) => {
    const { t } = useTranslation();
    const { state } = useLocation()

    const [selectedPickupDate, setSelectedPickupDate] = useState<PickupDatesOptions | null>(null);
    const { setCustomSearchData, ToggleReloadTable, clearField } = useContext(Context);

    const hospialId = sessionStorage.getItem("hopsitalId");
    const prevHospitalId = useRef<string | null>(hospialId);

    const { data: apiPickupDates } = useGet<PickupDates[]>({
        API: GetHospitalPickupLookup,
        depArr: [hospialId],
        params: [hospialId],
        isNotValid: !hospialId,
        defaultValue: [],
        needSpinner: false,
    });

    const pickupDatesOptions = useMemo(
        () =>
            apiPickupDates.map((data) => ({
                label: data.pickupDate,
                value: data.id,
                pickupDate: data.pickupDate,
                startDueDate: data.startDueDate,
                endDueDate: data.endDueDate,
                isDone: data.isDone,
            })),
        [apiPickupDates]
    );

    const filteredNotExpiredPickupDates = useMemo(() => {
        return pickupDatesOptions.filter((pickupDate) => !pickupDate.isDone);
    }, [pickupDatesOptions]);

    const handleSelect = (option: PickupDatesOptions | null) => {
        setSelectedPickupDate(option);
        setCustomSearchData((prev: any) => ({ ...prev, pickupId: option?.value }));
    };

    //for disable tabs if no pickup dates and return to default tab if change pharm
    useEffect(() => {
        const prevHospital = prevHospitalId.current;

        if (prevHospitalId && prevHospital !== hospialId) {
            setSelectedTab(REQUESTS_TABS.All);
            setSelectedPickupDate(null);
            setCustomSearchData((prev: any) => ({ ...prev, pickupId: null }));
        }

        if (!apiPickupDates.length) {
            setSelectedTab(REQUESTS_TABS.All);
            setDisabledTabs([
                REQUESTS_TABS.Preparing,
                REQUESTS_TABS.preparedAndReadyForDelivery,
            ]);
            setSelectedPickupDate(null);
        } else {
            setDisabledTabs([]);
        }

        prevHospitalId.current = hospialId;
    }, [apiPickupDates, hospialId]);

    //for handle tab change
    useEffect(() => {
        if (
            ![REQUESTS_TABS.All, REQUESTS_TABS.Expired].includes(selectedTab) &&
            filteredNotExpiredPickupDates.length &&
            !selectedPickupDate
        ) {
            setSelectedPickupDate(filteredNotExpiredPickupDates[0]);
            setCustomSearchData((prev: any) => ({
                ...prev,
                pickupId: filteredNotExpiredPickupDates[0]?.value,
            }));
        }
    }, [selectedTab, filteredNotExpiredPickupDates, setCustomSearchData, selectedPickupDate]);

    useEffect(() => {
        if (!selectedPickupDate && [REQUESTS_TABS.Preparing, REQUESTS_TABS.preparedAndReadyForDelivery].includes(selectedTab)) return;
        ToggleReloadTable((prev) => !prev);
    }, [selectedTab, selectedPickupDate]);

    //for handle clear search
    useEffect(() => {
        setSelectedPickupDate(null);
        setSelectedTab(REQUESTS_TABS.All);
    }, [clearField])

    const remainingDays = useMemo(
        () => getRemainingDays(selectedPickupDate?.pickupDate),
        [selectedPickupDate]
    );

    const getWarningType = () => {
        if (remainingDays === null) return null;
        if (remainingDays < 0) return "expired";
        if (remainingDays <= 2) return "urgent";
        if (remainingDays <= 5) return "warning";
        return "info";
    };

    const warningType = getWarningType();

    return (
        <div className="pickupContainter">
            <div className="pickup-header row align-items-center g-3 ">
                <div className="col-md-7 col-12">
                    <DropDown
                        val={selectedPickupDate}
                        setVal={handleSelect}
                        options={pickupDatesOptions}
                        label="hospitalPickupLookup"
                    />
                </div>
                <div className="col-md col-12 d-flex justify-content-md-end ">
                    {remainingDays !== null && (
                        <div className={`pickup-remaining pickup-remaining--${warningType}`}>
                            {remainingDays < 0
                                ? t("pickupExpired")
                                : t("remainingDays", { count: remainingDays })}
                        </div>
                    )}
                </div>
            </div>

            {
                !state?.pharmName?.value &&
                <Alert variant="danger" className="my-2">
                    {t("mustSelectPharmacyOption")}
                </Alert>
            }
            {selectedPickupDate && warningType && (
                <Alert variant={warningType} className="my-2">
                    {warningType === "expired" && t("pickupExpiredWarning")}
                    {warningType === "urgent" && t("pickupUrgentWarning")}
                    {warningType === "warning" && t("pickupNearDeadlineWarning")}
                    {warningType === "info" && t("pickupInfoWarning")}
                </Alert>
            )}

            <div className="pickupDates">
                <div className="d-block">
                    <p className="pickupDates-label">{t("patientReceivingPeriod")}:</p>
                </div>
                <div className="me-2 pickupDate-range">
                    <div className="pickupDates-item">
                        <i className="fas fa-calendar-check pickup-icon"></i>
                        <span className="pickupDates-label">{t("startPickupDate")}:</span>
                        <span className="pickupDates-value">
                            {dateFormatter(selectedPickupDate?.startDueDate, "ar", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                            })}
                        </span>
                    </div>
                    <div className="pickupDates-item">
                        <i className="fas fa-calendar-times pickup-icon"></i>
                        <span className="pickupDates-label">{t("endPickupDate")}:</span>
                        <span className="pickupDates-value">
                            {dateFormatter(selectedPickupDate?.endDueDate, "ar", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                            })}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PickupCard;
